/**
 * Utility class for Morse code conversion and timing
 */
export class MorseCodeUtils {
    // Morse code timing constants (in milliseconds) - Slowed down for subtlety
    static DOT_DURATION = 350;    // Longer dot duration
    static DASH_DURATION = 900;    // Longer dash duration
    static ELEMENT_GAP = 400;      // Longer gap between dots and dashes
    static LETTER_GAP = 1200;      // Longer gap between letters
    static WORD_GAP = 2800;        // Longer gap between words
    
    // Morse code dictionary
    static MORSE_CODE = {
        'A': '.-', 'B': '-...', 'C': '-.-.', 'D': '-..', 'E': '.', 'F': '..-.', 'G': '--.', 'H': '....', 'I': '..', 'J': '.---',
        'K': '-.-', 'L': '.-..', 'M': '--', 'N': '-.', 'O': '---', 'P': '.--.', 'Q': '--.-', 'R': '.-.', 'S': '...', 'T': '-',
        'U': '..-', 'V': '...-', 'W': '.--', 'X': '-..-', 'Y': '-.--', 'Z': '--..',
        '0': '-----', '1': '.----', '2': '..---', '3': '...--', '4': '....-', '5': '.....', '6': '-....', '7': '--...', '8': '---..',
        '9': '----.',
        '.': '.-.-.-', ',': '--..--', '?': '..--..', "'": '.----.', '!': '-.-.--', '/': '-..-.', '(': '-.--.', ')': '-.--.-',
        '&': '.-...', ':': '---...', ';': '-.-.-.', '=': '-...-', '+': '.-.-.', '-': '-....-', '_': '..--.-', '"': '.-..-.', 
        '$': '...-..-', '@': '.--.-.'
    };
    
    /**
     * Converts a string to Morse code
     * @param {string} text - Text to convert to Morse code
     * @returns {string} - Morse code representation
     */
    static textToMorse(text) {
        // Convert text to uppercase for matching with our dictionary
        text = text.toUpperCase();
        
        let morseCode = '';
        
        for (let i = 0; i < text.length; i++) {
            const char = text[i];
            
            // Handle spaces between words
            if (char === ' ') {
                morseCode += ' / '; // Word separator
                continue;
            }
            
            // Look up the character in our Morse code dictionary
            if (this.MORSE_CODE[char]) {
                morseCode += this.MORSE_CODE[char];
                
                // Add space between letters (if not the last character)
                if (i < text.length - 1 && text[i + 1] !== ' ') {
                    morseCode += ' ';
                }
            }
        }
        
        return morseCode;
    }
    
    /**
     * Converts a Morse code string to a sequence of blink timings
     * @param {string} morseCode - Morse code to convert
     * @returns {Array} - Array of objects with type and duration
     */
    static morseToBlinkSequence(morseCode) {
        const sequence = [];
        const elements = morseCode.split('');
        
        for (let i = 0; i < elements.length; i++) {
            const element = elements[i];
            
            if (element === '.') {
                // Dot - short blink
                sequence.push({ type: 'blink', duration: this.DOT_DURATION });
                
                // Add gap after element if not the last one
                if (i < elements.length - 1 && elements[i + 1] !== ' ' && elements[i + 1] !== '/') {
                    sequence.push({ type: 'pause', duration: this.ELEMENT_GAP });
                }
            } 
            else if (element === '-') {
                // Dash - long blink
                sequence.push({ type: 'blink', duration: this.DASH_DURATION });
                
                // Add gap after element if not the last one
                if (i < elements.length - 1 && elements[i + 1] !== ' ' && elements[i + 1] !== '/') {
                    sequence.push({ type: 'pause', duration: this.ELEMENT_GAP });
                }
            }
            else if (element === ' ') {
                // Space between letters
                sequence.push({ type: 'pause', duration: this.LETTER_GAP });
            }
            else if (element === '/') {
                // Space between words
                sequence.push({ type: 'pause', duration: this.WORD_GAP });
                
                // Skip the spaces around the slash
                if (i > 0 && elements[i - 1] === ' ') {
                    sequence.pop(); // Remove the letter gap we just added
                }
                if (i < elements.length - 1 && elements[i + 1] === ' ') {
                    i++; // Skip the next space
                }
            }
        }
        
        return sequence;
    }
    
    /**
     * Generates a complete blink sequence for a message
     * @param {string} message - The message to convert to a blink sequence
     * @returns {Array} - Array of blink timing objects
     */
    static generateBlinkSequence(message) {
        const morseCode = this.textToMorse(message);
        return this.morseToBlinkSequence(morseCode);
    }
}
